import api from './api'
import { normalizeGestorFeedbackEvidencePath } from '@/components/Gestores/gestorFeedbackEvidenceUtils'
import type { EntradaStepKey } from '@/components/Gestores/gestorClienteEntradaConfig'
import type { EntradaSteps } from '@/components/Gestores/gestorClienteEntradaConfig'
import type {
  GestorDashboardFilterCatalog,
  GestorDashboardFilterCriteria,
  GestorDashboardFilterPreset,
} from '@/components/Gestores/gestorDashboardPresetTypes'
import type {
  GestorDashboardPayload,
  GestorDashboardQuery,
  GestorDashboardTaskDetail,
} from '@/components/Gestores/gestorDashboardTypes'

export type GestorClienteRow = {
  id: string
  nome: string
  razaoSocial: string | null
  cnpj: string | null
  email: string | null
  telefone: string | null
  status: string
  updatedAt: string
  _count: { financeProjects: number; deals: number }
}

export type GestorClienteDetail = {
  id: string
  nome: string
  razaoSocial: string | null
  cnpj: string | null
  email: string | null
  telefone: string | null
  status: string
  clienteAtivos: Array<{
    id: string
    status: string
    createdAt: string
    deal: { id: string; nome: string; etapa: string; dataFechamento: string | null } | null
    closer: { id: string; name: string }
  }>
  projetos: Array<{
    id: string
    name: string | null
    status: string
    source: string
    createdAt: string
    deal: { id: string; nome: string; etapa: string; dataFechamento: string | null } | null
    escopo: {
      plataforma: string | null
      servicosContratados: string | null
      description: string | null
      responsavelAssinatura: string | null
      responsavelFinanceiro: string | null
      itensProposta: unknown
    }
  }>
  chamados: Array<{
    id: string
    protocol: string
    type: string
    area: string
    status: string
    slaDueAt: string | null
    createdAt: string
  }>
  historicoFluxos: Array<{
    id: string
    tipo: 'deal' | 'chamado'
    titulo: string
    detalhe: string
    data: string
  }>
}

export type GestorEquipeRow = {
  id: string
  nomeCompleto: string
  email: string | null
  telefone: string
  setor: string | null
  areaAtuacao: string | null
  remuneracaoMensal: number | null
  status: string
  dataInicioContrato: string | null
  _count: { ferias: number; pagamentos: number }
}

export type GestorChamadoRow = {
  id: string
  protocol: string
  type: string
  area: string
  status: string
  slaDueAt: string | null
  createdAt: string
  updatedAt?: string
  assignee?: { id: string; name: string } | null
}

export type GestorEntradaRow = {
  id: string
  empresaId: string
  dealId: string
  financeProjectId: string | null
  setor: string
  status: 'EM_ANDAMENTO' | 'CONCLUIDO'
  servicosLabel: string | null
  steps: EntradaSteps
  slaHandoverDueAt: string | null
  concludedAt: string | null
  createdAt: string
  updatedAt: string
  progress: { done: number; total: number }
  empresa: { id: string; nome: string; cnpj: string | null; plataforma: string | null }
  deal: {
    id: string
    nome: string
    etapa: string
    dataFechamento: string | null
    owner: { id: string; name: string; email: string }
  }
}

export type GestorEntradaDetail = GestorEntradaRow

export type GestorEquipeFeedbackTipo = 'ANOTACAO_PRIVADA' | 'FEEDBACK_PJ'

export type GestorFeedbackGestorOption = {
  id: string
  name: string
  role: string
  setor: string | null
}

export type GestorEquipeFeedback = {
  id: string
  tipo: GestorEquipeFeedbackTipo
  conteudo: string
  dataFeedback: string | null
  areaAtuacao: string | null
  situacao: string | null
  evidenciaUrl: string | null
  evidenciaFileName: string | null
  evidenciaMimeType: string | null
  evidenciaLink: string | null
  reportado: boolean
  dataReport: string | null
  observacoesReporte: string | null
  liberadoParaPj: boolean
  liberadoEm: string | null
  createdAt: string
  updatedAt: string
  author: { id: string; name: string }
}

export type GestorFeedbackDocumentPayload = {
  tipo?: GestorEquipeFeedbackTipo
  conteudo?: string
  authorId?: string
  dataFeedback?: string
  areaAtuacao?: string
  situacao?: string
  evidenciaUrl?: string | null
  evidenciaFileName?: string | null
  evidenciaMimeType?: string | null
  evidenciaLink?: string | null
  reportado?: boolean
  dataReport?: string | null
  observacoesReporte?: string | null
}

export type GestorEquipeDetail = GestorEquipeRow & {
  cpf: string | null
  cnpj: string | null
  razaoSocial: string | null
  cep: string | null
  logradouro: string | null
  numero: string | null
  complemento: string | null
  bairro: string | null
  cidade: string | null
  uf: string | null
  bancoNome: string | null
  bancoAgencia: string | null
  bancoConta: string | null
  bancoTipoConta: string | null
  pixChave: string | null
  observacoes: string | null
  portalUserId: string | null
  createdAt: string
  updatedAt: string
  pagamentos: Array<{
    id: string
    competencia: string
    valor: number
    pagoEm: string | null
    observacoes: string | null
    createdAt: string
  }>
  ferias: Array<{
    id: string
    dataInicio: string
    dataFim: string
    dias: number
    status: string
    liderancaAprovada: boolean
    observacoes: string | null
    createdAt: string
    createdBy: { id: string; name: string } | null
    aprovadoPor: { id: string; name: string } | null
  }>
  feriasResumo: {
    dataInicioContrato: string | null
    mesesParceria: number
    nfsEmitidas: number
    elegivelPorTempo: boolean
    elegivelPorNf: boolean
    elegivelDesde: string | null
    podeSolicitarFerias: boolean
    diasUsados: number
    diasRestantes: number
    motivoBloqueio: string | null
  }
  solicitacoesFerias: Array<{
    id: string
    protocol: string
    type: string
    status: string
    createdAt: string
    slaDueAt: string | null
    formData: Record<string, unknown>
    requester: { id: string; name: string; email: string } | null
  }>
  feedbacks: GestorEquipeFeedback[]
}

export type GestorJornadaSituacao = 'ATRASADA' | 'PROXIMA' | 'OK' | 'SEM_HISTORICO'

export type GestorJornadaMembro = {
  id: string
  nomeCompleto: string
  setor: string | null
  cadenciaDias: number
  ultimaOneOnOneEm: string | null
  proximaOneOnOneEm: string
  situacao: GestorJornadaSituacao
  feedbackResumo: {
    anotacoesPrivadas: number
    feedbackRascunho: number
    feedbackLiberado: number
  }
}

export type GestorJornadaDashboard = {
  resumo: {
    totalMembros: number
    atrasadas: number
    proximas7d: number
    feedbackRascunhos: number
  }
  proximosOneOnOne: GestorJornadaMembro[]
  feedbackPendente: Array<{ id: string; nomeCompleto: string; rascunhos: number }>
  membros: GestorJornadaMembro[]
}

export type GestorOneOnOneRegistro = {
  id: string
  realizadaEm: string
  notas: string | null
  author: { id: string; name: string }
  createdAt: string
}

export type GestorEquipeFeriasCalendario = {
  membros: Array<{
    id: string
    nomeCompleto: string
    setor: string | null
    situacao: 'elegivel' | 'pendente' | 'atrasada' | 'sem_saldo' | 'inativo'
    feriasResumo: GestorEquipeDetail['feriasResumo']
  }>
  eventos: Array<{
    id: string
    pjAgradaId: string
    nomeCompleto: string
    tipo: 'ferias_registrada' | 'solicitacao_p6' | 'solicitacao_p3'
    inicio: string
    fim: string
    dias: number | null
    status: string
    protocol: string | null
    requestId: string | null
  }>
}

export const gestoresService = {
  async listClientes(params?: { page?: number; search?: string }) {
    const { data } = await api.get<{ data: GestorClienteRow[]; meta: { totalItems: number } }>(
      '/gestores/clientes',
      { params },
    )
    return data
  },

  async getCliente(id: string) {
    const { data } = await api.get<{ data: GestorClienteDetail }>(`/gestores/clientes/${id}`)
    return data.data
  },

  async listEquipe(params?: { page?: number; search?: string; status?: string }) {
    const { data } = await api.get<{ data: GestorEquipeRow[]; meta: { totalItems: number } }>(
      '/gestores/equipe',
      { params },
    )
    return data
  },

  async getEquipeMember(id: string) {
    const { data } = await api.get<{ data: GestorEquipeDetail }>(`/gestores/equipe/${id}`)
    return data.data
  },

  async listEquipeFeriasCalendario() {
    const { data } = await api.get<{ data: GestorEquipeFeriasCalendario }>(
      '/gestores/equipe/ferias-calendario',
    )
    return data.data
  },

  async createFerias(
    pjId: string,
    body: { dataInicio: string; dataFim: string; dias: number; liderancaAprovada: boolean; observacoes?: string },
  ) {
    const { data } = await api.post(`/gestores/equipe/${pjId}/ferias`, body)
    return data.data
  },

  async addPagamento(
    pjId: string,
    body: { competencia: string; valor: number; pagoEm?: string; observacoes?: string },
  ) {
    const { data } = await api.post(`/gestores/equipe/${pjId}/pagamentos`, body)
    return data.data
  },

  async updateRemuneracao(pjId: string, body: { remuneracaoMensal: number | null }) {
    const { data } = await api.patch(`/gestores/equipe/${pjId}/remuneracao`, body)
    return data.data
  },

  async createFeedback(pjId: string, body: GestorFeedbackDocumentPayload) {
    const { data } = await api.post<{ data: GestorEquipeFeedback }>(
      `/gestores/equipe/${pjId}/feedback`,
      body,
    )
    return data.data
  },

  async updateFeedback(pjId: string, feedbackId: string, body: GestorFeedbackDocumentPayload) {
    const { data } = await api.patch<{ data: GestorEquipeFeedback }>(
      `/gestores/equipe/${pjId}/feedback/${feedbackId}`,
      body,
    )
    return data.data
  },

  async listFeedbackGestores() {
    const { data } = await api.get<{ data: GestorFeedbackGestorOption[] }>('/gestores/feedback/gestores')
    return data.data
  },

  async uploadFeedbackEvidence(pjId: string, file: File) {
    const formData = new FormData()
    formData.append('evidencia', file)
    const { data } = await api.post<{
      data: { fileUrl: string; fileName: string; mimeType: string }
    }>(`/gestores/equipe/${pjId}/feedback/evidencia`, formData)
    return {
      evidenciaUrl: normalizeGestorFeedbackEvidencePath(data.data.fileUrl),
      evidenciaFileName: data.data.fileName,
      evidenciaMimeType: data.data.mimeType,
    }
  },

  async liberarFeedback(pjId: string, feedbackId: string) {
    const { data } = await api.post<{ data: GestorEquipeFeedback }>(
      `/gestores/equipe/${pjId}/feedback/${feedbackId}/liberar`,
    )
    return data.data
  },

  async deleteFeedback(pjId: string, feedbackId: string) {
    await api.delete(`/gestores/equipe/${pjId}/feedback/${feedbackId}`)
  },

  async listJornada() {
    const { data } = await api.get<{ data: GestorJornadaDashboard }>('/gestores/jornada')
    return data.data
  },

  async updateJornadaCadencia(pjId: string, cadenciaDias: 30 | 60 | 90) {
    const { data } = await api.patch<{ data: { cadenciaDias: number; proximaOneOnOneEm: string } }>(
      `/gestores/jornada/${pjId}/cadencia`,
      { cadenciaDias },
    )
    return data.data
  },

  async registerOneOnOne(pjId: string, body: { realizadaEm: string; notas?: string }) {
    const { data } = await api.post<{ data: { proximaOneOnOneEm: string } }>(
      `/gestores/jornada/${pjId}/one-on-one`,
      body,
    )
    return data.data
  },

  async listOneOnOnes(pjId: string) {
    const { data } = await api.get<{ data: GestorOneOnOneRegistro[] }>(
      `/gestores/jornada/${pjId}/one-on-one`,
    )
    return data.data
  },

  async listChamados(params?: {
    queueLifecycle?: 'ativos' | 'pausados' | 'cancelados' | 'historico'
    area?: string
    search?: string
  }) {
    const { data } = await api.get<{ data: GestorChamadoRow[] }>('/gestores/chamados', { params })
    return data.data
  },

  async listEntradas(params?: { status?: 'EM_ANDAMENTO' | 'CONCLUIDO'; search?: string }) {
    const { data } = await api.get<{ data: GestorEntradaRow[] }>('/gestores/entradas', { params })
    return data.data
  },

  async getEntrada(id: string) {
    const { data } = await api.get<{ data: GestorEntradaDetail }>(`/gestores/entradas/${id}`)
    return data.data
  },

  async updateEntradaStep(
    id: string,
    stepKey: EntradaStepKey,
    body: { done: boolean; note?: string | null },
  ) {
    const { data } = await api.patch<{ data: GestorEntradaDetail }>(
      `/gestores/entradas/${id}/steps/${stepKey}`,
      body,
    )
    return data.data
  },

  async getDashboard(params: GestorDashboardQuery = {}) {
    const { data } = await api.get<{ data: GestorDashboardPayload }>('/gestores/dashboard', {
      params: {
        ...params,
        refresh: params.refresh ? 'true' : undefined,
      },
    })
    return data.data
  },

  async listDashboardFilterPresets(params: { createdFrom?: string; createdTo?: string } = {}) {
    const { data } = await api.get<{ data: GestorDashboardFilterPreset[] }>(
      '/gestores/dashboard/filter-presets',
      { params },
    )
    return data.data
  },

  async getDashboardFilterCatalogs(refresh = false) {
    const { data } = await api.get<{ data: GestorDashboardFilterCatalog }>(
      '/gestores/dashboard/filter-catalogs',
      { params: refresh ? { refresh: 'true' } : undefined },
    )
    return data.data
  },

  async createDashboardFilterPreset(body: {
    name: string
    criteria: GestorDashboardFilterCriteria
    isDefault?: boolean
  }) {
    const { data } = await api.post<{ data: GestorDashboardFilterPreset }>(
      '/gestores/dashboard/filter-presets',
      body,
    )
    return data.data
  },

  async updateDashboardFilterPreset(
    id: string,
    body: {
      name?: string
      criteria?: GestorDashboardFilterCriteria
      isDefault?: boolean
    },
  ) {
    const { data } = await api.patch<{ data: GestorDashboardFilterPreset }>(
      `/gestores/dashboard/filter-presets/${id}`,
      body,
    )
    return data.data
  },

  async deleteDashboardFilterPreset(id: string) {
    await api.delete(`/gestores/dashboard/filter-presets/${id}`)
  },

  async getDashboardTask(
    taskId: number,
    params: { createdFrom?: string; createdTo?: string; squadId?: number } = {},
  ) {
    const { data } = await api.get<{ data: GestorDashboardTaskDetail }>(
      `/gestores/dashboard/tasks/${taskId}`,
      { params },
    )
    return data.data
  },
}
